import React, { useState, useEffect } from 'react';
import { TrendingUp, Building, CreditCard, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  originalAmount: number;
  currency: string;
  showTitle?: boolean;
}

export default function AdminTransactionBreakdown({ originalAmount, currency, showTitle = true }: Props) {
  const [platformFeeRate, setPlatformFeeRate] = useState(0); 
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0); 
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadFeeRates();
  }, []);
  
  const loadFeeRates = async () => {
    try {
      setLoading(true);
      
      // Get platform fee rate
      const { data: platformFeeData, error: platformFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .single();
        
      if (!platformFeeError && platformFeeData) {
        setPlatformFeeRate(parseFloat(platformFeeData.value) / 100);
      } else {
        // Default fallback
        setPlatformFeeRate(0.20);
      }
      
      // Get gateway fee rate
      const { data: gatewayFeeData, error: gatewayFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .single();
        
      if (!gatewayFeeError && gatewayFeeData) {
        setGatewayFeeRate(parseFloat(gatewayFeeData.value) / 100);
      } else {
        // Default fallback
        setGatewayFeeRate(0.20);
      }
    } catch (error) {
      console.error('Error loading fee rates:', error);
      // Fall back to default values if there's an error
      setPlatformFeeRate(0.20);
      setGatewayFeeRate(0.20);
    } finally {
      setLoading(false);
    }
  };

  const platformFee = originalAmount * platformFeeRate;
  const gatewayFee = originalAmount * gatewayFeeRate;
  const totalFees = platformFee + gatewayFee;
  const clientAmount = originalAmount; // Amount to transfer to client
  const ourRevenue = platformFee; // Our platform fee
  const gatewayAmount = gatewayFee; // Amount to payment gateway
  const totalAmount = originalAmount + totalFees;

  const formatAmount = (amount: number) => {
    return `${currency} ${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl">
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl space-y-6">
      {showTitle && (
        <div className="flex items-center space-x-2 text-white">
          <TrendingUp className="w-5 h-5" />
          <span className="font-semibold text-lg">Transaction Breakdown</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Client Transfer */}
        <div className="bg-green-900/30 border border-green-700 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <ArrowRight className="w-4 h-4 text-green-400" />
            <span className="text-green-200 font-medium">Transfer to Client</span>
          </div>
          <div className="space-y-2">
            <p className="text-2xl font-bold text-green-400">{formatAmount(clientAmount)}</p>
            <p className="text-green-300 text-sm">Original item amount</p>
          </div>
        </div>

        {/* Our Revenue */}
        <div className="bg-blue-900/30 border border-blue-700 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <Building className="w-4 h-4 text-blue-400" />
            <span className="text-blue-200 font-medium">Our Revenue</span>
          </div>
          <div className="space-y-2">
            <p className="text-2xl font-bold text-blue-400">{formatAmount(ourRevenue)}</p>
            <p className="text-blue-300 text-sm">Platform fee ({(platformFeeRate * 100).toFixed(2)}%)</p>
          </div>
        </div>

        {/* Gateway Fee */}
        <div className="bg-purple-900/30 border border-purple-700 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <CreditCard className="w-4 h-4 text-purple-400" />
            <span className="text-purple-200 font-medium">Gateway Fee</span>
          </div>
          <div className="space-y-2">
            <p className="text-2xl font-bold text-purple-400">{formatAmount(gatewayAmount)}</p>
            <p className="text-purple-300 text-sm">Payment processing ({(gatewayFeeRate * 100).toFixed(2)}%)</p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-900/50 p-4 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Customer Paid</p>
            <p className="text-white font-medium">{formatAmount(totalAmount)}</p>
          </div>
          <div>
            <p className="text-gray-400">Total Fees</p>
            <p className="text-white font-medium">{formatAmount(totalFees)}</p>
          </div>
          <div>
            <p className="text-gray-400">Fee Percentage</p>
            <p className="text-white font-medium">{((totalFees / originalAmount) * 100).toFixed(2)}%</p>
          </div>
          <div>
            <p className="text-gray-400">Net to Client</p>
            <p className="text-white font-medium">{formatAmount(clientAmount)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}